import React, {useCallback, useEffect, useRef, useState} from "react";
import {BrowserRouter} from "react-router-dom";
import {SwitchRouteComponent} from "./Components";
import {
  CurrentTabInProgressAPIs,
  generateUUIDV4,
  MiddlewareHelper,
  SetGlobalAccountReducer,
  SetGlobalAccountUuidReducer,
  SetGlobalCompanyId,
  SetGlobalFullAccess,
  SetGlobalInactivityHandler,
  SetGlobalIsLoading,
  SetGlobalRerender,
  showError,
  ThemeInit,
} from "./Helpers";
import {AppRoutes} from "./Routes";
import {useDispatch, useSelector} from "react-redux";
import {
  updatePermissions,
  UpdatePortal,
  updateSelectedBranch,
} from "./Store/Actions";
import i18next from "i18next";
import moment from "moment";
import "moment/locale/ar";
import "moment/locale/tr";
import "moment/locale/ro";
import Fab from "@mui/material/Fab";
import {SystemVariablesEnum} from "./enums";
import {ThirdPartiesEnum} from "./enums/Shared/ThirdParties.Enum";
import VitallyIntegration from "./utils/Vitally.Helper";
import {useEventListener} from "./Hooks";
import {ScheduleTokenRefresh} from "./Helpers/Token.Helper";
import {useInactivityLogout} from "./Helpers/InactivityLogout.Hook";
import {Backdrop, CircularProgress} from "@mui/material";
import {GetAllPortalData} from "./Services/SetupsPortal.Services";
import {useTranslation} from "react-i18next";
import {GoogleReCaptchaProvider} from 'react-google-recaptcha-v3'
import {updateOnBrandCssVariables} from "./utils/contrastColor.utility";

const jiraServiceLink =
  "https://elevatus.atlassian.net/servicedesk/customer/portals";
const App = () => {
  const {t} = useTranslation("Shared");
  const [render, setRender] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  SetGlobalIsLoading(setIsLoading);
  moment.locale(i18next.language);
  const dispatch = useDispatch();
  const {updateTheHookHandler} = useInactivityLogout(undefined, dispatch);
  SetGlobalInactivityHandler(updateTheHookHandler);
  const selectedBranchReducer = useSelector(
    (state) => state?.SelectedBranchReducer
  );
  const branchesReducer = useSelector((state) => state?.BranchesReducer);
  const isInitRef = useRef(true);
  const loginResponse = useSelector(
    (state) => state.LoginReducers.loginResponse
  );
  SetGlobalRerender(setRender, render);

  const getThePortalData = useCallback(
    async (isFromLayout) => {
      if (!isFromLayout || !loginResponse || !loginResponse.token) return;
      const response = await GetAllPortalData({
        language_uuid: loginResponse?.language?.find(
          (item) => item.code === i18next.language
        )?.id,
      });
      if (response && response.status === 200) {
        dispatch(UpdatePortal(response.data.results));
        ThemeInit(response.data.results);
      } else showError(t("Shared:failed-to-get-saved-data"), response);
    },
    [dispatch, loginResponse, t]
  );

  useEffect(() => {
    if (localStorage.getItem("company_uuid"))
      SetGlobalCompanyId(localStorage.getItem("company_uuid"));
    if (localStorage.getItem("account_uuid"))
      SetGlobalAccountUuidReducer(localStorage.getItem("account_uuid"));
    if (localStorage.getItem("permissionsReducer"))
      SetGlobalFullAccess(
        JSON.parse(localStorage.getItem("permissionsReducer"))?.full_access
      );
    const localUser = localStorage.getItem("system_user")
      ? JSON.parse(localStorage.getItem("system_user"))
      : undefined;
    if (localUser) {
      SetGlobalAccountReducer(localUser);
      if (localUser.with_session_expiry_control) {
        updateTheHookHandler({
          newTimeoutDuration: localUser.session_expiry * 60 * 60 * 1000,
          newTurnOffInactivityLogoutValue: false,
        });

        return;
      }
    }
    updateTheHookHandler({
      newTimeoutDuration: undefined,
      newTurnOffInactivityLogoutValue: true,
    });
  }, [updateTheHookHandler]);
  // this is to update the permissions & check the active branch on change the branches reducer
  useEffect(() => {
    if (
      branchesReducer &&
      branchesReducer.branches &&
      branchesReducer.branches.results
    ) {
      const {
        branches: {results},
      } = branchesReducer;
      let branch_uuid = null;
      if (isInitRef.current) {
        // this to change the active branch if the user is logged in and the company_uuid is in the query
        // and the active branch is not the same as the company_uuid(if the user has access to that new branch)
        const url = new URL(window.location.href);
        const query = new URLSearchParams(url.search);
        branch_uuid = query.get("company_uuid");
      }
      isInitRef.current = false;
      const localSelectedBranch = results.find(
        (branch) =>
          selectedBranchReducer &&
          branch.uuid === selectedBranchReducer.uuid &&
          (!branch_uuid || branch_uuid === branch.uuid)
      );
      if (!localSelectedBranch || !localSelectedBranch.can_access) {
        const firstAccessibleBranch =
          results.find((branch) =>
            branch_uuid
              ? branch.uuid === branch_uuid && branch.can_access
              : branch.can_access
          ) || results.find((branch) => branch.can_access);
        if (!firstAccessibleBranch) return;
        dispatch(updateSelectedBranch(firstAccessibleBranch));
        dispatch(
          updatePermissions({
            permissions: firstAccessibleBranch.permission || [],
            full_access: firstAccessibleBranch.full_access,
          })
        );
        SetGlobalFullAccess(firstAccessibleBranch.full_access);

        return;
      }
      dispatch(
        updatePermissions({
          permissions: localSelectedBranch.permission || [],
          full_access: localSelectedBranch.full_access,
        })
      );
      SetGlobalFullAccess(localSelectedBranch.full_access);
    }
  }, [branchesReducer, dispatch, selectedBranchReducer]);

  useEffect(() => {
    if (
      selectedBranchReducer &&
      selectedBranchReducer.brandStyle &&
      (selectedBranchReducer.brandStyle.mainColor ||
        selectedBranchReducer.brandStyle.logo)
    ) {
      const root = document.querySelector(":root");
      if (selectedBranchReducer.brandStyle.mainColor)
        Object.values(SystemVariablesEnum)
          .filter((item) => item.isReflected)
          .map((item) =>
            root.style.setProperty(
              item.key,
              selectedBranchReducer.brandStyle.mainColor
            )
          );

      localStorage.setItem(
        "lastSelectedBranchTheme",
        JSON.stringify({
          ...(selectedBranchReducer.brandStyle || {}),
        })
      );
    } else {
      const lastSelectedBranchTheme =
        (localStorage.getItem("lastSelectedBranchTheme") &&
          JSON.parse(localStorage.getItem("lastSelectedBranchTheme"))) ||
        {};
      if (lastSelectedBranchTheme.mainColor) {
        const root = document.querySelector(":root");
        Object.values(SystemVariablesEnum)
          .filter((item) => item.isReflected)
          .map((item) =>
            root.style.setProperty(item.key, lastSelectedBranchTheme.mainColor)
          );
      }
    }
    updateOnBrandCssVariables();
  }, [selectedBranchReducer]);

  useEffect(() => {
    if (
      loginResponse &&
      !window.location.pathname.includes("/accounts/login") &&
      !Object.values(ThirdPartiesEnum).some((item) =>
        window.location.pathname.includes(item.path)
      )
    ) {
      window?.ChurnZero?.push([
        "setAppKey",
        process.env.REACT_APP_CHURNZERO_KEY,
      ]);
      window?.ChurnZero?.push([
        "setContact",
        loginResponse?.account_uuid,
        loginResponse?.uuid,
      ]);
    }
  }, [loginResponse]);

  // to change the leader tab for token refresh on refresh or duplicate the tab
  useEffect(() => {
    const focusedTabId = generateUUIDV4();
    sessionStorage.setItem("tabID", focusedTabId);
    localStorage.setItem("tokenLeaderTab", focusedTabId);
    ScheduleTokenRefresh(dispatch);
  }, [dispatch]);

  // this is to remove the leader from the current active tab its leader
  useEventListener("beforeunload", () => {
    // another logic for token refresh handler
    const tokenLeaderTab = localStorage.getItem("tokenLeaderTab");
    if (tokenLeaderTab && tokenLeaderTab === sessionStorage.getItem("tabID"))
      localStorage.removeItem("tokenLeaderTab");

    // a counter for the progress APIs
    const requestsCounter = localStorage.getItem("APIRequestsCounter")
      ? +localStorage.getItem("APIRequestsCounter")
      : 0;
    localStorage.setItem(
      "APIRequestsCounter",
      requestsCounter - CurrentTabInProgressAPIs + ""
    );
  });

  useEffect(() => {
    void getThePortalData(true);
  }, [getThePortalData, selectedBranchReducer]);

  return (
    <BrowserRouter>
      <GoogleReCaptchaProvider
        useEnterprise
        scriptProps={{
          async: true, // optional, default to false,
          defer: true, // optional, default to false
          appendTo: "head", // optional, default to "head", can be "head" or "body",
          nonce: undefined, // optional, default undefined
        }}
        reCaptchaKey={process.env.REACT_APP_GOOGLE_KEY}
      >
        <MiddlewareHelper />
        {isLoading && (
          <Backdrop
            className="spinner-wrapper"
            style={{zIndex: 9999}}
            open={isLoading}
          >
            <CircularProgress color="inherit" size={50} />
          </Backdrop>
        )}
        <Fab
          color="primary"
          size="small"
          sx={{
            zIndex: 1,
            border: "3px solid white !important",
            height: "50px!important",
            boxShadow: "none!important",
            width: "50px!important",
            position: "fixed",
            bottom: {xs: 56, md: 16},
            right: i18next.dir() === "ltr" ? 16 : "unset",
            left: i18next.dir() === "ltr" ? "unset" : 16,
          }}
          className="bg-primary help-icon-btn"
          onClick={() => window.open(jiraServiceLink)}
        >
          <span
            className="mdi mdi-help fab"
            style={{
              fontSize: "21px",
              transform: `rotateY(${i18next.dir() === "ltr" ? "0" : "180"}deg)`,
            }}
          />
        </Fab>
        <SwitchRouteComponent routes={AppRoutes} />
        <VitallyIntegration />
      </GoogleReCaptchaProvider>
    </BrowserRouter>
  );
};

export default App;
